/** Mirrors src/TeamPortal/Models/Sessions/SessionsPageData.cs */

import { t } from 'utilities/TranslationUtils';

export type SessionKindGroupKey =
  | 'freekick'
  | 'penalty'
  | 'corner'
  | 'shooting'
  | 'goalkeeper'
  | 'throwin';

export type SessionKindGroupName =
  | 'FreeKick'
  | 'Penalty'
  | 'Corner'
  | 'Shooting'
  | 'Goalkeeper'
  | 'ThrowIn';

export type SessionsPagePeriodKey = '2w' | '1m' | '3m' | '6m' | 'all';

/** Declarative period options — extend by adding a row here AND in the backend SessionsPagePeriods.All. */
export const PERIOD_OPTIONS: { value: SessionsPagePeriodKey; labelKey: string; days: number }[] = [
  { value: '2w',  labelKey: 'sessions.periodLast2Weeks',  days: 14 },
  { value: '1m',  labelKey: 'sessions.periodLast1Month',  days: 30 },
  { value: '3m',  labelKey: 'sessions.periodLast3Months', days: 90 },
  { value: '6m',  labelKey: 'sessions.periodLast6Months', days: 180 },
  { value: 'all', labelKey: 'sessions.periodAllData',     days: Number.MAX_SAFE_INTEGER },
];

/**
 * Per-kind chart capability flags — used by the page to decide which charts to show.
 * Add a row here when adding a new kind to keep the visibility logic declarative.
 */
export type KindChartCapability = {
  /** Show the top-down pitch chart? */
  pitch: boolean;
  /** When the pitch chart is shown for THIS kind, prefer landing mode over kick origin. */
  /** Show the goal-front-view chart? */
  goalView: boolean;
  /** Which zone selector to display, if any. */
  zoneSelector: 'freekick' | 'corner' | false;
  /**
   * Minimum kicks a session must have to appear as a point on the on-target trend.
   * High-volume kinds (FreeKick, Shooting, Goalkeeper) use 3 to filter noisy single-kick sessions.
   * Low-volume-per-session kinds (Corner, Penalty, ThrowIn) use 1 so every session is shown.
   */
  minKicksForTrend: number;
};

/** Declarative kind options — extend by adding a row here, in the backend SessionKindGrouping, AND in CAPABILITIES below. */
export const KIND_OPTIONS: { value: SessionKindGroupKey; labelKey: string; group: SessionKindGroupName }[] = [
  { value: 'freekick',   labelKey: 'sessions.kindFreeKick',   group: 'FreeKick' },
  { value: 'penalty',    labelKey: 'sessions.kindPenalty',    group: 'Penalty' },
  { value: 'shooting',   labelKey: 'sessions.kindShooting',   group: 'Shooting' },
  { value: 'corner',     labelKey: 'sessions.kindCorner',     group: 'Corner' },
  { value: 'goalkeeper', labelKey: 'sessions.kindGoalkeeper', group: 'Goalkeeper' },
  { value: 'throwin',    labelKey: 'sessions.kindThrowIn',    group: 'ThrowIn' },
];

export const KIND_CAPABILITIES: Record<SessionKindGroupName, KindChartCapability> = {
  FreeKick:   { pitch: true, goalView: true,  zoneSelector: 'freekick', minKicksForTrend: 1 },
  Penalty:    { pitch: true, goalView: true,  zoneSelector: false,       minKicksForTrend: 1 },
  Corner:     { pitch: true, goalView: false, zoneSelector: 'corner',    minKicksForTrend: 1 },
  Shooting:   { pitch: true, goalView: true,  zoneSelector: false,       minKicksForTrend: 1 },
  Goalkeeper: { pitch: true, goalView: false, zoneSelector: false,       minKicksForTrend: 1 },
  ThrowIn:    { pitch: true, goalView: false, zoneSelector: false,       minKicksForTrend: 1 },
};

/**
 * Kinds whose ball does not go at the goal (corner, throw-in, goalkeeper) have no goal front view: everywhere —
 * player views, session cards, the session page — they open on the pitch top view instead. Derived from
 * `KIND_CAPABILITIES.goalView`, the one place that is declared.
 */
export function prefersTopView(kind: string | null | undefined): boolean {
  return !!kind && kind in KIND_CAPABILITIES && !KIND_CAPABILITIES[kind as SessionKindGroupName].goalView;
}

export interface SessionsPageDto {
  summary: SessionsSummaryDto;
  measurements: MeasurementMarkerDto[];
  periodMeasurements: MeasurementMarkerDto[];
  availableSessions: SessionOptionDto[];
  availablePlayers: PlayerOptionDto[];
  periodMeta: PeriodMetaDto;
  /** Aggregate metadata about the snapshot itself. Null when no snapshot data is available. */
  snapshotMeta: SnapshotMetaDto | null;
  selectionCap: number;
  freeKickZones: FreeKickZoneDto[];
  /** Corner zone layouts (per layout × side) for drawing the zone selector. */
  cornerZoneLayouts: CornerZoneLayoutDto[];
}

/** Aggregate metadata read from the snapshot's own header (generation time + org-wide counts). */
export interface SnapshotMetaDto {
  generatedAtUtc: string;
  sessionCount: number;
  playerCount: number;
  teamCount: number;
  clubCount: number;
  measurementCount: number;
  latestMeasurementUtc: string | null;
}

export interface MeasurementMarkerDto {
  id: string;
  sessionId: string;
  playerId: string;
  playerName: string;
  kind: SessionKindGroupName;
  kindRaw: string;
  result: string;
  capturedAtUtc: string;
  kickX: number | null;
  kickZ: number | null;
  /** FreeKick kicking-zone id (1..9), or null when the kick is not in a zone. */
  zoneId?: number | null;
  /** FreeKick zone id per layout id ("1" = 9, "2" = 6, "3" = 5 zones). */
  freeKickZoneIds?: Record<string, number> | null;
  /** Corner zones: map from layout id (e.g. "1", "2", "8") to zone id. Corner side is derived from sign of kickZ. */
  cornerZones?: Record<string, number> | null;
  goalY: number | null;
  goalZ: number | null;
  landingX: number | null;
  landingZ: number | null;
  ballSpeed: number | null;
  distance?: number | null;
  hangTime: number | null;
  gFactor?: number | null;
  spinRate?: number | null;
  spinAxis?: number | null;
  launchAngle?: number | null;
  launchDirection?: number | null;
  pullDirection?: number | null;
  maxHeight?: number | null;
  goalCrossingSpeed?: number | null;
  wallCrossingHeight?: number | null;
  reachableRegionStart: [number, number] | null;
  reachableRegionEnd: [number, number] | null;
  reachableRegionStartTime?: number | null;
  reachableRegionEndTime?: number | null;
  onTarget: number | null;
  trajectorySamples: number[][] | null;
  isFavorite?: boolean | null;
  /** Ids of attached videos — used to build blob video URLs client-side with a preloaded SAS token. */
  videoIds?: string[];
  /** Null = unknown (snapshot lacks video info). */
  videoCount: number | null;
}

export interface FreeKickZoneDto {
  /** Layout this zone belongs to ("1" = 9, "2" = 6, "3" = 5 zones). Zone ids are unique per layout. */
  layoutId: string;
  layoutName: string;
  zoneId: number;
  /** Angle (rad) of the column centre — label anchor. */
  centreAngle: number;
  /** Half the angular width (rad) available to the label. */
  halfSpan: number;
  /** "Left" | "Centre" | "Right" (layout 1), "Left" | "Right" (layout 2) or "All" (layout 3) */
  column: string;
  /** 0-based distance band within the layout (0 = nearest the penalty box). */
  bandIndex: number;
  /** Band inner/outer radius (m) from the centre of the goal line. */
  innerRadius: number;
  outerRadius: number;
  /** Closed polygon outline as [x, z] points in football coords (z NOT flipped). */
  outline: [number, number][];
}

/** Which corner a kick was taken from — zones are mirrored per side. */
export type CornerSide = 'Left' | 'Right';

export interface CornerZoneDto {
  zoneId: number;
  /** Closed polygon outline as [x, z] points in football coords (z NOT flipped). */
  outline: [number, number][];
}

export interface CornerZoneLayoutDto {
  /** Layout id — e.g. "1", "2", "8" for the SDK-supported templates. */
  layoutId: string;
  /** Human-friendly name e.g. "TrackMan Standard". */
  layoutName: string;
  side: CornerSide;
  /** The zones (rectangles) for this layout+side combination. */
  zones: CornerZoneDto[];
}

export interface SessionOptionDto {
  id: string;
  capturedAtUtc: string;
  kind: SessionKindGroupName;
  playerCount: number;
  kickCount: number;
  measurementIds: string[];
  /** Player ids in this session — populated on the landing recent-session list so the selection
   *  screen can render per-player recent cards without measurements. Empty on the player page. */
  playerIds?: string[];
  /** Live kick count per player id in this session (landing recent list only). */
  playerKickCounts?: Record<string, number>;
  /** Per player id, the kicks the session-card previews are drawn from (landing recent list only). */
  playerPreviews?: Record<string, SessionPreviewPointDto[]>;
}

/** The few per-kick values a session-card preview needs; a MeasurementMarkerDto satisfies it too. */
export interface SessionPreviewPointDto {
  id: string;
  result: string;
  kickX: number | null;
  kickZ: number | null;
  goalY: number | null;
  goalZ: number | null;
  landingX: number | null;
  landingZ: number | null;
  reachableRegionStart: [number, number] | null;
  reachableRegionEnd: [number, number] | null;
  onTarget: number | null;
}

export interface PlayerKindStatsDto {
  kickCount: number;
  sessionCount: number;
  onTargetPct: number;
  /** Null for binary kinds (Corner, ThrowIn, Goalkeeper). */
  goalPct: number | null;
  /** Average ball speed in km/h. Null when no speed data is available. */
  avgBallSpeed: number | null;
  avgKicksPerSession: number;
  /** Average spin rate in rpm. Null when no spin data is available. */
  avgSpinRate: number | null;
  /** Average pull direction in degrees. Null when no pull-direction data is available. */
  avgPullDirection: number | null;
  /** Average wall-crossing height in metres (free kicks only). Null otherwise. */
  avgWallCrossingHeight: number | null;
}

export interface PlayerOptionDto {
  id: string;
  name: string;
  teamName: string;
  clubName: string;
  /** Read-SAS URL of the player's profile picture; absent when they have none. */
  avatarUrl?: string | null;
  kickCount: number;
  /** Number of sessions the player appears in — powers the landing roster ranking + chip counts. */
  sessionCount?: number;
  measurementIds: string[];
  /** Per-kind aggregated stats keyed by SessionKindGroup name (e.g. "FreeKick"). */
  kindStats?: Partial<Record<SessionKindGroupName, PlayerKindStatsDto>>;
}

export interface PeriodMetaDto {
  windowStart: string;
  windowEnd: string;
  comparisonStart: string;
  comparisonEnd: string;
  isStaleSnapshot: boolean;
  latestMeasurementUtc: string | null;
  periodKey: SessionsPagePeriodKey;
  kindsKey: string;
}

export interface SessionsSummaryDto {
  sections: SummarySection[];
}

export type SummarySectionId =
  | 'noActivity'
  | 'staleData'
  | 'volume'
  | 'topPerformers'
  | 'accuracy'
  | 'momentum'
  | 'anomaly'
  | 'coverage';

export interface SummarySection {
  id: SummarySectionId;
  fragments: SummaryFragment[];
}

export interface SummaryFragment {
  text: string;
  bold: boolean;
  popoverKind?: string;
  popoverPayload?: unknown;
}

export function formatKindLabel(kind: SessionKindGroupName): string {
  const option = KIND_OPTIONS.find((o) => o.group === kind);
  return option ? t(option.labelKey) : kind;
}

export const ALL_KIND_KEYS: SessionKindGroupKey[] = KIND_OPTIONS.map((o) => o.value);
